import React, { useState, useEffect } from "react";
import { Container, Form, Button, Alert } from "react-bootstrap";
import useQRScanner from "../utils/useQrCode";
import { VideoScanBar, VideoWrapper } from "../components/Login.style";
import NavbarTop from "../components/NavbarTop";
import NavbarBottom from "../components/NavbarBottom";
import { useNavigate } from "react-router-dom";
import { fetchAPI } from "../utils/fetchAPI";
import { getEntityTypeDisplayName } from "../utils/entityType";

interface QrCode {
  qrCode: string;
  entityId: number;
  entityType: number;
  entityName: string;
}

const AddQrPage: React.FC = () => {
  const [qrCode, setQrCode] = useState("");
  const [entityId, setEntityId] = useState("");
  const [entityType, setEntityType] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isQrCodeValid, setIsQrCodeValid] = useState(true);
  const [grade, setGrade] = useState("");
  const [thickness, setThickness] = useState("");
  const [steelItems, setSteelItems] = useState<any[]>([]);

  const { videoRef, qrCode: scannedQrCode, setQrCode: setScannedQrCode, error: cameraError } = useQRScanner();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSteelItems = async () => {
      try {
        const data = await fetchAPI("GET", "steel.php");
        if (data?.success) {
          setSteelItems(data.data);
        }
      } catch (error) {
        console.error("Error fetching steel items:", error);
      }
    };

    fetchSteelItems();
  }, []);

  useEffect(() => {
    const checkQrCode = async () => {
      if (!scannedQrCode) return;

      setIsLoading(true);
      setErrorMessage("");
      setSuccessMessage("");

      try {
        const data = await fetchAPI("POST", "checkqr.php", { qr: scannedQrCode });

        if (data?.success) {
          setQrCode("");
          setErrorMessage("Kod QR już jest wykorzystany");
          setIsQrCodeValid(false);
        } else {
          setQrCode(scannedQrCode);
          setIsQrCodeValid(true);
        }
      } catch (error) {
        setErrorMessage("Błąd podczas sprawdzania kodu QR");
        setIsQrCodeValid(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkQrCode();
  }, [scannedQrCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isQrCodeValid) return;

    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const result = await fetchAPI("POST", "addQr.php", {
        qrCode,
        entityId: parseInt(entityId),
        entityType: parseInt(entityType),
        grade: entityType === "3" ? grade : undefined,
        thickness: entityType === "3" ? thickness : undefined,
      });

      if (result.success) {
        setSuccessMessage("Kod QR został dodany pomyślnie");
        setQrCode("");
        setEntityId("");
        setEntityType("");
        setGrade("");
        setThickness("");
        setScannedQrCode?.(null);
        setIsQrCodeValid(true);
      } else {
        setErrorMessage(result.message || "Wystąpił błąd podczas dodawania kodu QR");
      }
    } catch (error) {
      setErrorMessage("Wystąpił błąd podczas dodawania kodu QR");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <NavbarTop />
      <Container className="mt-4 mb-5">
        <h1 className="mb-4">Dodaj nowy kod QR</h1>

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Kod QR</Form.Label>
            {!qrCode && (
              <VideoWrapper>
                <video ref={videoRef} style={{ width: "100%" }} />
                <VideoScanBar />
              </VideoWrapper>
            )}
            {cameraError && <Alert variant="warning">{cameraError}</Alert>}
            {qrCode && isQrCodeValid && <Alert variant="success">Zaczytany kod QR: {qrCode}</Alert>}
            {qrCode && !isQrCodeValid && <Alert variant="danger">Kod QR już jest wykorzystany</Alert>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Typ encji</Form.Label>
            <Form.Select
              value={entityType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setEntityType(e.target.value);
                if (e.target.value === "3") {
                  setEntityId("");
                }
              }}
              required
            >
              <option value="">Wybierz typ encji</option>
              <option value="1">Pracownik</option>
              <option value="2">Urządzenie</option>
              <option value="3">Przekierowanie dla stali</option>
            </Form.Select>
          </Form.Group>

          {entityType !== "3" && (
            <Form.Group className="mb-3">
              <Form.Label>ID encji</Form.Label>
              <Form.Control
                type="number"
                value={entityId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEntityId(e.target.value)}
                required
              />
            </Form.Group>
          )}

          {entityType === "3" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Gatunek stali</Form.Label>
                <Form.Select
                  value={grade}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setGrade(e.target.value)}
                  required
                >
                  <option value="">Wybierz gatunek stali</option>
                  {Array.from(new Set(steelItems.map((item) => item.grade))).map((grade) => (
                    <option key={grade} value={grade}>
                      {grade}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Grubość (mm)</Form.Label>
                <Form.Select
                  value={thickness}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setThickness(e.target.value)}
                  required
                >
                  <option value="">Wybierz grubość</option>
                  {Array.from(new Set(steelItems.map((item) => item.thickness)))
                    .sort((a, b) => a - b)
                    .map((thickness) => (
                      <option key={thickness} value={thickness}>
                        {thickness}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </>
          )}

          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={() => navigate("/qr-management")}>
              Anuluj
            </Button>
            <Button variant="primary" type="submit" disabled={isLoading || !isQrCodeValid}>
              {isLoading ? "Dodawanie..." : "Dodaj"}
            </Button>
          </div>
        </Form>
      </Container>
      <NavbarBottom />
    </>
  );
};

export default AddQrPage;
